/**
 * usePageMetaOverride — fetch admin-authored meta override for the current path.
 * Caches per (path, lang) in-memory to avoid re-fetching during SPA navigation.
 * Silently no-ops on error — PageHead always has a hardcoded fallback.
 */
import { useEffect, useState } from "react";
import { fetchPublicPageMeta } from "../services/seoAuditService";

const cache = new Map();

function normalizePath(p) {
  if (!p || typeof p !== "string") return "/";
  let s = p.trim();
  if (!s.startsWith("/")) s = "/" + s;
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  return s.toLowerCase();
}

/**
 * @param {string} canonical — canonical path passed to PageHead (with or without lang prefix)
 * @param {string} lang — current language
 * @returns override object or null
 */
export function usePageMetaOverride(canonical, lang) {
  const [override, setOverride] = useState(null);

  useEffect(() => {
    if (!canonical) return;
    // Strip lang prefix if present
    let path = canonical;
    try {
      if (path.startsWith("http")) path = new URL(path).pathname;
    } catch { /* ignore */ }
    const parts = path.split("/").filter(Boolean);
    if (parts[0] && ["en", "ru", "he"].includes(parts[0])) {
      path = "/" + parts.slice(1).join("/");
    }
    path = normalizePath(path);

    const key = `${path}::${lang || "en"}`;
    if (cache.has(key)) {
      setOverride(cache.get(key));
      return;
    }

    let cancelled = false;
    fetchPublicPageMeta(path, lang || "en")
      .then((data) => {
        if (cancelled) return;
        cache.set(key, data || null);
        setOverride(data || null);
      })
      .catch(() => {
        if (cancelled) return;
        cache.set(key, null);
        setOverride(null);
      });

    return () => { cancelled = true; };
  }, [canonical, lang]);

  return override;
}
